import React from "react";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";

/**
 * Apple-style KPI tile.
 *
 * Hairline border instead of shadow. Top row pairs a small monochrome icon
 * with a sentence-case label (regular weight, no letter-spacing). Below sits
 * the value at a hero size with tightened tracking. Interactive tiles get a
 * faint top-right chevron that brightens and nudges right on hover — the
 * same tactile cue Apple uses for "tap for more".
 *
 * The whole tile is a button — tapping it opens the metric drill-down modal.
 * The card no longer renders an "i" tooltip inside its header because the
 * tap target conflicted with the card's onClick on mobile (the modal
 * always swallowed the tap before the tooltip popper could open). The
 * tooltip copy now lives inside MetricDrillModal as a visible "About"
 * section at the top of the body.
 */
const MetricCard = ({
  title,
  value,
  icon,
  onClick,
  disabled,
  sublabel,
  sublabelMuted,
}) => {
  const display = value === null || value === undefined || Number.isNaN(value) ? "—" : value;
  const isInteractive = Boolean(onClick) && !disabled;

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      focusRipple
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        textAlign: "left",
        // !important defeats global `button { border-radius: 50px !important }`
        // in src/index.css that otherwise pill-rounds every <button>, including
        // ButtonBase, regardless of sx. Established workaround in this codebase.
        borderRadius: "14px !important",
        border: "1px solid rgba(0,0,0,0.06)",
        bgcolor: "#fff",
        p: { xs: 2, sm: 2.5 },
        transition: "background 160ms ease, transform 120ms ease, border-color 160ms ease",
        "&:hover": {
          bgcolor: "rgba(0,0,0,0.02)",
          borderColor: "rgba(0,0,0,0.1)",
        },
        "&:hover .metric-card-chevron": {
          opacity: 0.75,
          transform: "translateX(2px)",
        },
        "&:active": { transform: "scale(0.99)" },
        "&.Mui-disabled": { opacity: 0.5 },
      }}
    >
      <Stack spacing={{ xs: 1, sm: 1.25 }} sx={{ width: "100%" }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ pr: isInteractive ? 2.5 : 0, minWidth: 0 }}
        >
          {icon ? (
            <Box
              sx={{
                color: "rgba(0,0,0,0.5)",
                display: "flex",
                flexShrink: 0,
                "& svg": { fontSize: 16 },
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Typography
            noWrap
            sx={{
              color: "rgba(0,0,0,0.5)",
              fontWeight: 500,
              fontSize: "0.8125rem",
              lineHeight: 1.2,
              letterSpacing: 0,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Typography
          sx={{
            fontSize: { xs: "1.375rem", sm: "1.625rem" },
            fontWeight: 600,
            color: "rgba(0,0,0,0.88)",
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
          }}
        >
          {display}
        </Typography>

        {sublabel ? (
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "rgba(0,0,0,0.5)",
              opacity: sublabelMuted ? 0.5 : 1,
              lineHeight: 1.3,
            }}
          >
            {sublabel}
          </Typography>
        ) : null}
      </Stack>

      {isInteractive ? (
        <ChevronRight
          className="metric-card-chevron"
          sx={{
            position: "absolute",
            top: { xs: 15, sm: 19 },
            right: { xs: 11, sm: 15 },
            fontSize: 18,
            color: "rgba(0,0,0,0.3)",
            opacity: 0.45,
            transition: "opacity 160ms ease, transform 160ms ease",
            pointerEvents: "none",
          }}
        />
      ) : null}
    </ButtonBase>
  );
};

export default MetricCard;
